import { forwardRef, useState, useRef, useEffect } from "react";
import {
  ActionBarPrimitive,
  AttachmentPrimitive,
  AuiIf,
  ThreadPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useThreadRuntime,
  useThreadComposerAttachment,
  useMessageAttachment,
  useMessage,
} from "@assistant-ui/react";
import { StreamdownTextPrimitive } from "@assistant-ui/react-streamdown";
import { code } from "@streamdown/code";
import { mermaid } from "@streamdown/mermaid";
import { useTranslation } from "react-i18next";
import {
  ArrowUp,
  Paperclip,
  X,
  FileText,
  FileSpreadsheet,
  Bot,
  User,
  Copy,
  CheckCheck,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  ChevronRight,
  Square,
  Sparkles,
  Brain,
} from "lucide-react";
import ToolCallStep from "./ToolCallStep";
import OpcionesForm from "./OpcionesForm";
import { METADATA_MODELO, METADATA_PREGUNTAS_SUGERIDAS, modoFormulario, preguntasSugeridasDe } from "@/lib/preguntas";
import SelectorModelo, { BadgeModelo } from "./SelectorModelo";
import { useChatState } from "./ChatRuntime";
import { desenvolverPrevia } from "@/lib/previa";
import { REHYPE_PLUGINS_CHAT } from "@/lib/rehypeChat";
import { agentIcon } from "@/lib/agentColors";
import { useAuth } from "@/lib/auth/useAuth";
import { useTheme } from "@/lib/theme/ThemeProvider";

/* ── Streaming-optimized markdown ── */

/* Official Streamdown fallback for a Mermaid block that fails to parse. The
   @streamdown/mermaid plugin re-throws on a partial/invalid diagram; without a
   fallback that throw would unmount the whole app (blank screen). During
   streaming the fenced ```mermaid block is momentarily incomplete and triggers
   this, so we show a neutral "generating" placeholder — when the block closes
   and becomes valid, the diagram renders in its place. */
function MermaidGenerating() {
  const { t } = useTranslation();
  return (
    <div className="my-2 animate-pulse rounded-lg border border-border bg-muted/40 px-3 py-4 text-xs text-muted-foreground">
      {t("streamdown.generatingDiagram")}
    </div>
  );
}

function StreamdownText() {
  const { t } = useTranslation();
  const translations = t("streamdown", { returnObjects: true }) as Record<string, string>;
  // Match the Mermaid diagram theme to the app's light/dark mode. With no theme
  // Mermaid uses its light-oriented "default", whose edge/line color is nearly
  // invisible on a dark background; "dark" makes the connectors readable.
  const { resolved } = useTheme();

  return (
    <StreamdownTextPrimitive
      // La previa del presupuesto llega a veces dentro de un ```markdown y se
      // pintaria como codigo fuente: ver lib/previa.ts.
      preprocess={desenvolverPrevia}
      // Solo imagenes de origenes propios: lib/imagenesChat.ts.
      rehypePlugins={REHYPE_PLUGINS_CHAT}
      caret="block"
      plugins={{ code, mermaid }}
      controls={{ table: false, mermaid: { download: false, fullscreen: true } }}
      mermaid={{
        errorComponent: MermaidGenerating,
        config: { theme: resolved === "dark" ? "dark" : "default" },
      }}
      translations={translations}
      remend={{
        bold: true,
        italic: true,
        boldItalic: true,
        inlineCode: true,
        strikethrough: true,
        links: true,
        images: true,
      }}
    />
  );
}

/* ── Sent file attachment (user message) ── */

function DocumentAttachment() {
  const attachment = useMessageAttachment();
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  const isImage =
    attachment.file?.type?.startsWith("image/") ||
    attachment.contentType?.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp)$/i.test(attachment.name ?? "");
  const isPdf =
    attachment.contentType === "application/pdf" ||
    attachment.name?.toLowerCase().endsWith(".pdf");
  const isDocx =
    attachment.contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    attachment.name?.toLowerCase().endsWith(".docx");
  const isXlsx =
    attachment.contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    attachment.name?.toLowerCase().endsWith(".xlsx");

  // Extract image URL from attachment content (for restored messages without File object)
  const attContent = (attachment as { content?: { type: string; image?: string }[] }).content;
  const contentImageUrl = attContent?.find((c) => c.type === "image")?.image;

  useEffect(() => {
    if (!isImage) return;
    // Use File object if available, otherwise contentImageUrl is used directly
    if (!attachment.file) return;
    const url = URL.createObjectURL(attachment.file);
    setThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [isImage, attachment.file]);

  const displayUrl = thumbUrl || contentImageUrl;

  return (
    <AttachmentPrimitive.Root className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--muted)] p-2 text-xs">
      {isImage && displayUrl ? (
        <img
          src={displayUrl}
          alt={attachment.name}
          className="h-12 w-12 shrink-0 rounded object-cover"
        />
      ) : isPdf ? (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-red-50">
          <FileText size={20} className="text-red-500" />
        </div>
      ) : isDocx ? (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-blue-50">
          <FileText size={20} className="text-blue-600" />
        </div>
      ) : isXlsx ? (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-green-50">
          <FileSpreadsheet size={20} className="text-green-600" />
        </div>
      ) : (
        <FileText size={16} className="shrink-0 text-[var(--primary)]" />
      )}
      <span className="max-w-[200px] truncate font-medium">
        <AttachmentPrimitive.Name />
      </span>
    </AttachmentPrimitive.Root>
  );
}

/* ── User message ── */

function UserMessage() {
  const { user } = useAuth();

  return (
    <MessagePrimitive.Root className="mx-auto flex max-w-3xl flex-col items-end gap-1 py-4 px-4">
      <div className="flex items-start gap-2">
        {user?.avatarUrl ? (
          <img
            src={user.avatarUrl}
            alt={user.name}
            className="h-6 w-6 shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--primary)]">
            <User className="h-3 w-3 text-white" />
          </div>
        )}
        <div className="rounded-3xl bg-[var(--secondary)] px-5 py-2.5 text-sm leading-relaxed">
          <div className="flex flex-wrap gap-2">
            <MessagePrimitive.Attachments
              components={{ Document: DocumentAttachment }}
            />
          </div>
          <MessagePrimitive.Parts>
            {({ part }) => {
              if (part.type === "text") return <StreamdownText />;
              return null;
            }}
          </MessagePrimitive.Parts>
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

/* ── Thumbs up / down — shown on every assistant message ── */

function MessageRating() {
  const { threadId, traceIds, ratings, setRating, api } = useChatState();
  const messageIndex = useMessage((s) => s.index);

  // assistant message index: user=0, assistant=1, user=2, assistant=3...
  const assistantIndex = Math.floor(messageIndex / 2);
  const myTraceId = traceIds[assistantIndex] ?? undefined;
  const rated = ratings[assistantIndex] ?? null;

  const handleRate = async (value: "ok" | "ko") => {
    setRating(assistantIndex, value);
    if (threadId) {
      try {
        await api.scoreThread(threadId, value, undefined, myTraceId);
      } catch (e) {
        console.error("Failed to score thread:", e);
      }
    }
  };

  if (rated) {
    return rated === "ok" ? (
      <ThumbsUp size={14} className="text-green-500" />
    ) : (
      <ThumbsDown size={14} className="text-[var(--primary)]" />
    );
  }

  return (
    <>
      <button
        onClick={() => handleRate("ok")}
        className="rounded p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-green-500"
        title="Buena respuesta"
      >
        <ThumbsUp size={14} />
      </button>
      <button
        onClick={() => handleRate("ko")}
        className="rounded p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--primary)]"
        title="Mala respuesta"
      >
        <ThumbsDown size={14} />
      </button>
    </>
  );
}

/* ── Copy icon with check state ── */

function CopyIcon() {
  return (
    <>
      <MessagePrimitive.If copied>
        <CheckCheck size={14} />
      </MessagePrimitive.If>
      <MessagePrimitive.If copied={false}>
        <Copy size={14} />
      </MessagePrimitive.If>
    </>
  );
}

/* ── Context info step (admin-only, collapsible) ── */

function ContextStep({ content }: { content: string }) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  // Extract only the summary text after the last [CONTEXTO PREVIO...] marker
  const markerIdx = content.lastIndexOf("[CONTEXTO PREVIO");
  const afterMarker = markerIdx >= 0
    ? content.slice(content.indexOf("]", markerIdx) + 1).trim()
    : content.trim();
  if (!afterMarker) return null;

  return (
    <div className="my-1.5 rounded-lg border border-[var(--border)] bg-[var(--muted)] text-sm">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-2 cursor-pointer hover:bg-[var(--border)]/30"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Brain size={13} className="shrink-0 text-purple-500" />
        <span className="text-[var(--muted-foreground)]">
          {t("tools.contextSummary", "Resumen de contexto")}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-[var(--border)] px-3 py-2">
          <pre className="whitespace-pre-wrap text-xs text-[var(--foreground)]">{afterMarker}</pre>
        </div>
      )}
    </div>
  );
}

/* ── Formulario SUGERIDO (ayuda de interfaz) ──
   No es un mensaje del agente ni una tool que el modelo haya llamado: el backend
   lo deriva de la PROSA ya escrita (`src/preguntas_sugeridas.py`) y lo manda como
   un evento aparte. Que las preguntas se lean también en el texto de arriba es lo
   ESPERADO — esto es solo el atajo para contestarlas. Por eso lleva una etiqueta
   discreta encima: sin ella se leería como si el agente hubiera vuelto a preguntar.

   No es una part: viaja en `metadata.custom` del mensaje del asistente, escrita
   por el stream en vivo (`sse.ts`) y por la carga del historial
   (`chatApi.getThreadMessages`, desde `suggested_questions` del state). Por eso se
   pinta DESPUÉS de `MessagePrimitive.Parts`, siempre debajo del texto, sin
   depender del orden de las parts.

   Mientras el run corre NO se pinta (montaría en lectura y parpadearía como
   "Cerrado" hasta `end`). Activo solo en el ÚLTIMO mensaje del thread: si el
   usuario ya respondió (con el formulario o escribiendo), queda en lectura. La
   decisión vive en `modoFormulario` (lib/preguntas.ts), que sí es testable. La
   respuesta se envía como un mensaje de usuario más: mismo camino (guardrail,
   lock, router) que el texto. */
function FormularioSugerido() {
  const sugeridas = useMessage((s) => s.metadata.custom?.[METADATA_PREGUNTAS_SUGERIDAS]);
  const isLast = useMessage((s) => s.isLast);
  const isRunning = useMessage((s) => s.status?.type === "running");
  const thread = useThreadRuntime();
  const formulario = preguntasSugeridasDe(sugeridas);
  const modo = modoFormulario({ isLast, isRunning });
  if (!formulario || !modo) return null;
  // Sin etiqueta propia: el pie del formulario ya dice "tambien puedes escribir
  // en el chat", y dos avisos seguidos se leian repetidos.
  return (
    <div className="mt-1">
      <OpcionesForm
        key={formulario.message_id}
        preguntas={formulario.preguntas}
        modo={modo}
        onEnviar={(texto) => thread.append({ role: "user", content: [{ type: "text", text: texto }] })}
      />
    </div>
  );
}

/* ── Badge del modelo (conector) ── */

/**
 * Badge "este mensaje corrio con X", solo para admins y solo cuando la
 * conversacion tiene un modelo fijado. El modelo por mensaje viaja en
 * `metadata.custom` desde los DOS productores (stream en vivo y carga del
 * historial), asi que sobrevive a recargar la pagina.
 */
function BadgeModeloMensaje() {
  const { user } = useAuth();
  const { modeloElegido } = useChatState();
  const modelo = useMessage((s) => s.metadata.custom?.[METADATA_MODELO]);
  if (user?.role !== "admin") return null;
  return (
    <BadgeModelo
      modelo={typeof modelo === "string" ? modelo : null}
      threadTieneOverride={modeloElegido !== null}
    />
  );
}

/* ── Assistant message ── */

function AssistantMessage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isLast = useMessage((s) => s.isLast);
  const isRunning = useMessage((s) => s.status?.type === "running");
  // `ThreadRuntime.append` devuelve `void` y se traga la promesa del core, así
  // que un fallo del envío (red, 401, 500) no vuelve a quien llamó: la única
  // señal es el estado del mensaje. Sin este aviso el fallo era MUDO — burbuja
  // vacía y nada más. Ver OpcionesForm y `fix(front)` de este cambio.
  const falloEnvio = useMessage(
    (s) => s.status?.type === "incomplete" && s.status?.reason === "error",
  );

  return (
    <MessagePrimitive.Root className="mx-auto max-w-3xl px-4 py-2 group/msg">
      <div className="flex gap-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--fonestar-gray-dark,#53565A)]">
          <Bot className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0 flex-1 pt-1 text-sm leading-relaxed [&>*:first-child]:mt-0 [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-lg [&_h1]:font-bold [&_h2]:mt-3 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-bold [&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:font-bold [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
          {/* Thinking indicator — shown before first content arrives */}
          <AuiIf condition={(s) => s.message.parts.length === 0 && s.message.status?.type === "running"}>
            <div className="flex items-center gap-1.5 py-2">
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--muted-foreground)]" style={{ animationDelay: "0ms" }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--muted-foreground)]" style={{ animationDelay: "150ms" }} />
              <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[var(--muted-foreground)]" style={{ animationDelay: "300ms" }} />
            </div>
          </AuiIf>

          <MessagePrimitive.Parts>
            {({ part }) => {
              if (part.type === "text") return <StreamdownText />;
              if (part.type === "tool-call") {
                if (part.toolName === "__context__") {
                  return isAdmin ? <ContextStep content={String(part.result ?? "")} /> : null;
                }
                return <ToolCallStep {...part} isAdmin={isAdmin} />;
              }
              return null;
            }}
          </MessagePrimitive.Parts>

          <FormularioSugerido />

          {falloEnvio && (
            <div
              role="alert"
              className="my-2 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-[var(--foreground)]"
            >
              {t("chat.messageFailed")}
            </div>
          )}

          {/* Copy + Rating — altura reservada siempre para que el hover no
              desplace el contenido; solo togglea opacidad en mensajes antiguos. */}
          <div className={`mt-2 flex h-7 items-center gap-1 transition-opacity ${isLast ? "opacity-100" : "opacity-0 group-hover/msg:opacity-100"} ${isRunning ? "opacity-0 pointer-events-none" : ""} ${!isLast ? "pointer-events-none group-hover/msg:pointer-events-auto" : ""}`}>
            <ActionBarPrimitive.Copy asChild>
              <button className="rounded p-1 text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]">
                <CopyIcon />
              </button>
            </ActionBarPrimitive.Copy>
            <MessageRating />
            <BadgeModeloMensaje />
          </div>
          <MessagePrimitive.If last>
            {isAdmin && <UsageBar />}
          </MessagePrimitive.If>
        </div>
      </div>
    </MessagePrimitive.Root>
  );
}

/* ── Usage bar (admin-only, discreet) ── */

function UsageBar() {
  const { tokenUsage } = useChatState();
  const { input, output, cached, cost } = tokenUsage;
  if (!input && !output) return null;

  const total = input + output;
  const cachePercent = input > 0 ? Math.round((cached / input) * 100) : 0;

  const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

  return (
    <div className="mt-1.5 flex items-center gap-2 text-[10px] text-[var(--muted-foreground)] opacity-60">
      <span>{fmt(total)} tokens</span>
      <span>({fmt(input)} in · {fmt(output)} out)</span>
      {cachePercent > 0 && <span>⚡ {cachePercent}% cached</span>}
      {cost > 0 && <span>· ${cost < 0.01 ? cost.toFixed(4) : cost.toFixed(3)}</span>}
    </div>
  );
}

/* ── Composer attachment preview ── */

function ComposerAttachment() {
  const attachment = useThreadComposerAttachment();
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  const isImage = /^image\/(png|jpe?g|gif|webp)$/i.test(
    attachment.contentType ?? "",
  );
  const isPdf =
    attachment.contentType === "application/pdf" ||
    attachment.name?.toLowerCase().endsWith(".pdf");
  const isDocx =
    attachment.contentType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    attachment.name?.toLowerCase().endsWith(".docx");
  const isXlsx =
    attachment.contentType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    attachment.name?.toLowerCase().endsWith(".xlsx");

  useEffect(() => {
    if (!isImage || !attachment.file) return;
    const url = URL.createObjectURL(attachment.file);
    setThumbUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [isImage, attachment.file]);

  return (
    <AttachmentPrimitive.Root className="flex items-center gap-2 rounded-lg bg-[var(--muted)] px-3 py-1.5 text-sm">
      {isImage && thumbUrl ? (
        <img
          src={thumbUrl}
          alt={attachment.name}
          className="h-12 w-12 shrink-0 rounded object-cover"
        />
      ) : isPdf ? (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-red-50">
          <FileText size={20} className="text-red-500" />
        </div>
      ) : isDocx ? (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-blue-50">
          <FileText size={20} className="text-blue-600" />
        </div>
      ) : isXlsx ? (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-green-50">
          <FileSpreadsheet size={20} className="text-green-600" />
        </div>
      ) : (
        <FileText size={14} className="shrink-0 text-[var(--muted-foreground)]" />
      )}
      <span className="max-w-[200px] truncate">
        <AttachmentPrimitive.Name />
      </span>
      <AttachmentPrimitive.Remove className="ml-1 rounded p-0.5 hover:bg-[var(--border)]">
        <X size={12} />
      </AttachmentPrimitive.Remove>
    </AttachmentPrimitive.Root>
  );
}

/* ── Agent icon helper ── */

// El mapa de iconos/colores por agente vive en `@/lib/agentColors` (fuente
// unica compartida con el Sidebar y la bandeja de aprobaciones).

function AgentIcon({ agentId, size = 40 }: { agentId: string; size?: number }) {
  const meta = agentIcon(agentId);
  const Icon = meta.icon;
  const iconSize = Math.round(size * 0.5);
  return (
    <div
      className={`flex shrink-0 items-center justify-center ${meta.color}`}
      style={{ width: size, height: size }}
    >
      <Icon size={iconSize} />
    </div>
  );
}

/* ── Agent selector dropdown ── */

function AgentSelector() {
  const { t } = useTranslation();
  const { agents, assistantId, onChangeAgent } = useChatState();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!agents || agents.length <= 1) return null;

  // In auto mode (assistantId === "auto") no concrete agent is selected yet:
  // show an "Automatic" label instead of pretending the first agent is active.
  const current = agents.find((a) => a.graph_id === assistantId);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
          open
            ? "border-[var(--primary)] text-[var(--foreground)] ring-1 ring-[var(--primary)]"
            : "border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--muted-foreground)] hover:text-[var(--foreground)]"
        }`}
      >
        {current ? (
          <>
            <AgentIcon agentId={current.graph_id} size={18} />
            <span>{t(`agents.${current.graph_id}.label`, current.label)}</span>
          </>
        ) : (
          <>
            <Sparkles size={14} />
            <span>{t("chat.autoAgent")}</span>
          </>
        )}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute bottom-full right-0 mb-2 min-w-[240px] rounded-xl border border-[var(--border)] bg-[var(--background)] p-1.5 shadow-xl">
          {agents.map((agent) => (
            <button
              key={agent.graph_id}
              type="button"
              onClick={() => { onChangeAgent(agent.graph_id); setOpen(false); }}
              className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-[var(--muted)] ${
                agent.graph_id === assistantId ? "bg-[var(--muted)] font-semibold" : ""
              }`}
            >
              <AgentIcon agentId={agent.graph_id} size={28} />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-[var(--foreground)]">{t(`agents.${agent.graph_id}.label`, agent.label)}</div>
                <div className="truncate text-[11px] text-[var(--muted-foreground)]">{t(`agents.${agent.graph_id}.description`, agent.description)}</div>
              </div>
              {agent.graph_id === assistantId && (
                <svg className="h-4 w-4 shrink-0 text-[var(--primary)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Selector de modelo (conector: props <- contexto del chat) ── */

/**
 * Cablea `SelectorModelo` con el estado del chat. El selector en si es puro y
 * recibe todo por props para poder probarse sin montar el thread.
 */
function SelectorModeloChat() {
  const { user } = useAuth();
  const { agents, assistantId, modeloElegido, elegirModelo } = useChatState();
  const agente = agents.find((a) => a.graph_id === assistantId);
  // En modo auto todavia no hay agente resuelto, asi que no se sabe cual es su
  // modelo por defecto: pintar el selector ahi mostraria un default inventado.
  if (!agente) return null;
  return (
    <SelectorModelo
      isAdmin={user?.role === "admin"}
      modelos={agente.models_disponibles ?? []}
      modeloDefault={agente.model_default}
      modeloElegido={modeloElegido}
      onElegir={elegirModelo}
    />
  );
}

/* ── Composer ── */

const Composer = forwardRef<HTMLFormElement>(function Composer(_props, ref) {
  const { t } = useTranslation();
  const { attachmentError, clearAttachmentError, avisoModeloAdjunto, limpiarAvisoModeloAdjunto } =
    useChatState();
  const threadMessages = useThreadRuntime().getState().messages;
  const placeholder = threadMessages.length > 0 ? t("chat.placeholderActive") : t("chat.placeholder");

  // Auto-dismiss attachment error after 5 seconds
  useEffect(() => {
    if (!attachmentError) return;
    const timer = setTimeout(clearAttachmentError, 5000);
    return () => clearTimeout(timer);
  }, [attachmentError, clearAttachmentError]);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 pb-4">
      {attachmentError && (
        <div className="mb-2 flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          <span>{attachmentError}</span>
          <button onClick={clearAttachmentError} className="ml-2 shrink-0 rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900">
            <X size={12} />
          </button>
        </div>
      )}
      {/* El modelo elegido NO viaja por el camino de upload (multipart sin
          `config`): el turno corrio con el modelo que ya tuviera la
          conversacion. Se dice en voz alta — callarlo dejaria al admin sacando
          conclusiones sobre un modelo que no se uso. No se auto-cierra: es una
          correccion de lo que el usuario acaba de ver, no un aviso de paso. */}
      {avisoModeloAdjunto && (
        <div
          role="alert"
          className="mb-2 flex items-center justify-between rounded-lg border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-xs text-[var(--foreground)]"
        >
          <span>{t("chat.modelo.avisoAdjunto")}</span>
          <button
            onClick={limpiarAvisoModeloAdjunto}
            className="ml-2 shrink-0 rounded p-0.5 hover:bg-[var(--background)]"
            aria-label={t("chat.modelo.cerrarAviso")}
          >
            <X size={12} />
          </button>
        </div>
      )}
      <ComposerPrimitive.AttachmentDropzone
        className="rounded-3xl transition-colors data-[dragging]:ring-2 data-[dragging]:ring-[var(--primary)]/40"
      >
      <ComposerPrimitive.Root
        ref={ref}
        className="flex flex-col rounded-3xl border border-[var(--border)] bg-[var(--background)] focus-within:border-[var(--primary)] focus-within:ring-1 focus-within:ring-[var(--primary)]"
      >
        {/* Attachment previews — managed by assistant-ui */}
        <AuiIf condition={(s) => s.composer.attachments.length > 0}>
          <div className="flex flex-wrap gap-2 px-4 pt-3">
            <ComposerPrimitive.Attachments
              components={{
                Attachment: ComposerAttachment,
              }}
            />
          </div>
        </AuiIf>

        {/* Crece con el contenido hasta 18 lineas y despues hace scroll
            interno; el tope evita que un pegado largo se coma el hilo. */}
        <ComposerPrimitive.Input
          placeholder={placeholder}
          minRows={1}
          maxRows={18}
          className="w-full resize-none overflow-y-auto bg-transparent px-5 pt-3 pb-2 text-sm leading-6 text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:outline-none"
          autoFocus
        />

        <div className="flex items-center justify-between px-3 pb-2">
          <div className="flex items-center gap-1">
            <ComposerPrimitive.AddAttachment asChild>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--muted-foreground)] transition-colors hover:bg-[var(--muted)] hover:text-[var(--foreground)]"
                title={t("chat.attachFile")}
              >
                <Paperclip className="h-4 w-4" />
              </button>
            </ComposerPrimitive.AddAttachment>
          </div>

          <div className="flex items-center gap-2">
            <ThreadPrimitive.If empty>
              <AgentSelector />
            </ThreadPrimitive.If>

            {/* FUERA del `If empty` del selector de agente: el agente se fija al
                primer mensaje, pero el modelo se puede cambiar a mitad de la
                conversacion — que es justo para lo que sirve (probar uno y, si
                va bien, asignarlo). */}
            <SelectorModeloChat />

            <AuiIf condition={(s) => !s.thread.isRunning}>
              <ComposerPrimitive.Send
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-white shadow-sm transition-all hover:opacity-90 disabled:opacity-30"
                aria-label={t("chat.send")}
              >
                <ArrowUp className="h-4 w-4" />
              </ComposerPrimitive.Send>
            </AuiIf>

            <AuiIf condition={(s) => s.thread.isRunning}>
              <ComposerPrimitive.Cancel
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--primary)] text-white shadow-sm transition-all hover:opacity-90"
              >
                <Square className="h-3.5 w-3.5" />
              </ComposerPrimitive.Cancel>
            </AuiIf>
          </div>
        </div>
      </ComposerPrimitive.Root>
      </ComposerPrimitive.AttachmentDropzone>
    </div>
  );
});

/* ── Scroll to bottom ── */

function ScrollToBottom() {
  return (
    <ThreadPrimitive.ScrollToBottom className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 rounded-full border border-[var(--border)] bg-[var(--background)] p-1.5 shadow-sm transition-colors hover:bg-[var(--muted)]">
      <ChevronDown size={16} className="text-[var(--muted-foreground)]" />
    </ThreadPrimitive.ScrollToBottom>
  );
}

/* ── Empty state hero (greeting only, composer is rendered below) ── */

function EmptyHero() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userName = user?.name?.split(" ")[0] ?? "";

  return (
    <div className="flex flex-col items-center gap-3 px-4">
      <h1 className="text-2xl font-semibold text-[var(--foreground)]">
        {userName ? t("chat.greetingWithName", { name: userName }) : t("chat.greeting")}
      </h1>
    </div>
  );
}

/* ── Main thread ── */

export default function ChatThread() {
  const { threadId } = useChatState();

  return (
    <ThreadPrimitive.Root className="flex h-full flex-col bg-[var(--background)]">
      {/* Empty + brand-new thread: greeting and composer centered together
          (Gemini-style). When loading an existing thread we skip this to
          avoid the composer flashing in the center before history arrives. */}
      {!threadId && (
        <ThreadPrimitive.Empty>
          <div className="flex flex-1 items-center justify-center">
            <div className="flex w-full flex-col items-center gap-6">
              <EmptyHero />
              <div className="w-full">
                <Composer />
              </div>
            </div>
          </div>
        </ThreadPrimitive.Empty>
      )}

      {/* Active conversation (or loading history): scrollable messages on top,
          composer pinned to bottom. */}
      <ThreadPrimitive.If empty={false}>
        <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-3xl">
            <ThreadPrimitive.Messages
              components={{ UserMessage, AssistantMessage }}
            />
          </div>
        </ThreadPrimitive.Viewport>

        <ThreadPrimitive.ViewportFooter className="relative">
          <ScrollToBottom />
          <Composer />
        </ThreadPrimitive.ViewportFooter>
      </ThreadPrimitive.If>

      {/* Edge case: threadId set but messages still loading → keep the
          standard bottom-pinned composer so it doesn't jump. */}
      {threadId && (
        <ThreadPrimitive.Empty>
          <ThreadPrimitive.Viewport className="flex-1 overflow-y-auto" />
          <ThreadPrimitive.ViewportFooter className="relative">
            <ScrollToBottom />
            <Composer />
          </ThreadPrimitive.ViewportFooter>
        </ThreadPrimitive.Empty>
      )}
    </ThreadPrimitive.Root>
  );
}
